/**
 * Marketplace (WP4-MARKET, BUILD-PLAN M23): the module store, module pages,
 * trials and turn-on through Square, requests from staff, and Billing and
 * modules. Replaces the shell's Marketplace placeholder (same module id).
 *
 * Other modules gate their screens with `ModuleGate` / `MarketplaceLock`
 * from `./index.ts`.
 */
import { defineManifest } from '@velora/contracts';
import { defineWebModule } from '../../app/module.js';
import { registerSearchProvider } from '../../app/commands/registry.js';
import { marketplaceSearchProvider } from './palette.js';

registerSearchProvider(marketplaceSearchProvider);

export default defineWebModule({
  manifest: defineManifest({
    id: 'marketplace',
    title: 'Marketplace',
    version: '0.1.0',
    owner: 'WP4-MARKET',
    entitlementKey: null,
    permissions: ['marketplace.view', 'marketplace.purchase'],
    routes: [
      { id: 'store', path: '/marketplace', title: 'Marketplace', officeScope: 'practice' },
      {
        id: 'billing',
        path: '/marketplace/billing',
        title: 'Billing and modules',
        officeScope: 'practice',
        permission: 'marketplace.view',
      },
      {
        id: 'requests',
        path: '/marketplace/requests',
        title: 'Module requests',
        officeScope: 'practice',
      },
      {
        id: 'settings',
        path: '/settings/marketplace',
        title: 'Marketplace and billing',
        officeScope: 'practice',
      },
      {
        id: 'terms',
        path: '/marketplace/terms/$moduleKey',
        title: 'Module terms',
        officeScope: 'practice',
      },
      {
        id: 'module',
        path: '/marketplace/$moduleKey',
        title: 'Marketplace module',
        officeScope: 'practice',
      },
    ],
    // Zac 2026-10-28: Marketplace is always in the sidebar footer, above
    // Feature requests and Settings (sidenav FOOTER_IDS). It also opens from
    // the user menu, Settings > Marketplace and billing, the palette and G M.
    nav: [
      {
        label: 'Marketplace',
        group: 'marketplace',
        routeId: 'store',
        icon: 'store',
        order: 80,
        permission: 'marketplace.view',
      },
    ],
    commands: [
      {
        id: 'marketplace.open',
        title: 'Go to Marketplace',
        keywords: ['add-ons', 'modules', 'store', 'trial'],
        routeId: 'store',
      },
      {
        id: 'marketplace.billing',
        title: 'Billing and modules',
        keywords: ['invoices', 'card', 'usage', 'square', 'subscription'],
        routeId: 'billing',
        permission: 'marketplace.view',
      },
      {
        id: 'marketplace.requests',
        title: 'Module requests',
        keywords: ['request', 'approve', 'marketplace'],
        routeId: 'requests',
      },
    ],
    shortcuts: [{ keys: 'g m', description: 'Go to Marketplace', routeId: 'store' }],
  }),
  pages: {
    store: () => import('./pages/StorePage.js'),
    billing: () => import('./pages/BillingPage.js'),
    requests: () => import('./pages/RequestsPage.js'),
    module: () => import('./pages/ModulePage.js'),
    settings: () => import('./pages/SettingsPage.js'),
    terms: () => import('./pages/TermsPage.js'),
  },
});
